/**
 * The poke alert (2026-10-29): a poke is meant to get your attention, so it
 * is louder than the app's calm cues (sound/sounds.ts).
 *
 * - The buzz: an alarming three-pulse buzz synthesised with the Web Audio API
 *   (two detuned square and sawtooth oscillators through a low-pass, no
 *   audio file), with its own switch and volume (Settings > Notifications),
 *   persisted on the device like the other sound settings. On phones the
 *   same switch also vibrates (`navigator.vibrate`, where there is one).
 * - Outside the app (notify/notify.ts, kind "poke"): while the window is not
 *   focused, the web flashes the tab's title until it gets the focus and the
 *   desktop app flashes its taskbar button (Critical), both with "Flash
 *   taskbar"; and a sticky OS notification shows (`requireInteraction` on the
 *   web, the long duration on desktop) with "Enable desktop notifications".
 *
 * Silent pokes (an ignored sender, Do Not Disturb) never get here.
 */
import { create } from 'zustand';
import type { Snowflake } from '@neosocial/protocol';
import { storage } from '../lib/storage';
import { closeNotification, notify } from '../notify/notify';
import { audioContext } from '../sound/sounds';

export interface PokeAlertPrefs {
  /** The buzz (and the vibration on phones). */
  buzz: boolean;
  /** 0..1 */
  volume: number;
}

const KEY = 'poke-alert';
const DEFAULTS: PokeAlertPrefs = { buzz: true, volume: 0.8 };

interface PokeAlertStore {
  prefs: PokeAlertPrefs;
  setPrefs(p: Partial<PokeAlertPrefs>): void;
}

export const usePokeAlert = create<PokeAlertStore>((set) => ({
  prefs: { ...DEFAULTS, ...storage.getJson<Partial<PokeAlertPrefs>>(KEY, {}) },
  setPrefs(p) {
    set((st) => {
      const prefs = { ...st.prefs, ...p };
      storage.setJson(KEY, prefs);
      return { prefs };
    });
  },
}));

/* ---------------------------------------------------------------- the buzz */

/** One pulse of the buzz: start and length (s) and its pitch (Hz). */
interface Pulse {
  t: number;
  d: number;
  f: number;
}

/** "Bzz, bzz, bzzz": two short low pulses and a longer, higher one (~0.9 s). */
export const BUZZ: Pulse[] = [
  { t: 0, d: 0.15, f: 185 },
  { t: 0.21, d: 0.15, f: 185 },
  { t: 0.42, d: 0.42, f: 247 },
];

/** The master gain for a volume (0..1): a curve, so half sounds half as loud; square waves are loud. */
export function buzzGain(volume: number): number {
  return Math.pow(Math.max(0, Math.min(1, volume)), 1.6) * 0.42;
}

/** Plays the buzz if its switch is on. `preview` (Settings' play button) ignores the switch. */
export function playBuzz(o: { preview?: boolean } = {}): boolean {
  const { buzz, volume } = usePokeAlert.getState().prefs;
  if (!o.preview && (!buzz || volume <= 0)) return false;
  const v = o.preview && volume <= 0 ? 0.6 : volume;
  try {
    const c = audioContext();
    if (!c) return false;
    if (c.state === 'suspended') void c.resume().catch(() => {});
    synthBuzz(c, v);
  } catch {
    return false;
  }
  return true;
}

function synthBuzz(c: AudioContext, volume: number): void {
  const t0 = c.currentTime + 0.01;
  const master = c.createGain();
  master.gain.value = buzzGain(volume);
  // the harshest partials off: alarming, not piercing
  const lp = c.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.value = 2400;
  lp.connect(master);
  master.connect(c.destination);
  let end = t0;
  for (const p of BUZZ) {
    const s = t0 + p.t;
    const e = s + p.d;
    const g = c.createGain();
    g.gain.setValueAtTime(0.0001, s);
    g.gain.exponentialRampToValueAtTime(1, s + 0.006);
    g.gain.setValueAtTime(1, e - 0.03);
    g.gain.exponentialRampToValueAtTime(0.0001, e);
    g.connect(lp);
    // a square and a sawtooth a semitone apart beat against each other: the buzz
    for (const [type, f] of [
      ['square', p.f],
      ['sawtooth', p.f * 1.06],
    ] as const) {
      const o = c.createOscillator();
      o.type = type;
      o.frequency.setValueAtTime(f, s);
      o.connect(g);
      o.start(s);
      o.stop(e + 0.02);
    }
    end = Math.max(end, e);
  }
  setTimeout(
    () => {
      try {
        master.disconnect();
      } catch {
        /* already gone */
      }
    },
    (end - t0 + 0.2) * 1000,
  );
}

/** The vibration on phones (Android; iOS has none): buzz, buzz, buzz. */
export const VIBRATION = [260, 120, 260, 120, 520];

function vibrate(): void {
  if (!usePokeAlert.getState().prefs.buzz) return;
  try {
    if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function')
      navigator.vibrate(VIBRATION);
  } catch {
    /* not allowed without a user gesture on some browsers */
  }
}

/* ---------------------------------------------------- outside the app */

export { flashTitle, stopTitleFlash } from '../notify/title';

const tagOf = (key: string) => 'poke-' + key;

/** Closing the poke's card closes its OS notification. */
export function closePokeNotice(key: string): void {
  closeNotification(tagOf(key));
}

/**
 * Everything a poke that pops up does besides its card: the buzz, the
 * vibration, and (through notify) the title or taskbar flash and the OS
 * notification.
 */
export function alertPoke(p: { key: string; title: string; body: string; from?: Snowflake }): void {
  playBuzz();
  vibrate();
  notify({
    kind: 'poke',
    title: p.title,
    body: p.body,
    from: p.from ?? null,
    tag: tagOf(p.key),
    // the buzz is its sound
    silent: true,
    sticky: true,
  });
}
